import { ApolloClient, createHttpLink, ApolloLink, InMemoryCache } from '@apollo/client/core';
import moment from 'moment';
import Cookies from 'js-cookie';

import { onError } from '@apollo/client/link/error';
import store from './store';
import { getLoginUrl } from './utils/auth';

// Config
const defaultOptions = {
  tokenName: 'token',
  // Enable Automatic Query persisting with Apollo Engine
  persisting: false,
  // Use websockets for everything (no HTTP)
  websocketsOnly: false,
  // Is being rendered on the server?
  ssr: false,
  // Remove __typename from mutations
  addTypename: false,

  // Additional ApolloClient options
  apollo: {
    queryDeduplication: false,
    defaultOptions: {
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore'
      }
    }
  }
};

const headersLink = new ApolloLink((operation, forward) => {
  const headers = {};

  const token = Cookies.get('CF_Authorization') || null;
  const version = store.state.version ? store.state.version.name : null;

  const headerSettings = {
    authorization: `Bearer ${token}`,
    mode: 'draft',
    ts: moment().unix(),
    organization: 'WEBTALES',
    application: 'UCPA-PIM'
  };

  if (version) {
    headerSettings.version = version;
  }

  if (operation.getContext()?.headers?.mode) {
    headerSettings.mode = operation.getContext().headers.mode;
  }

  if (operation.getContext()?.headers?.disableTs) {
    delete headerSettings.ts;
  }

  const locale = operation.getContext()?.headers?.locale || store.state.locale;
  if (locale) {
    headerSettings.locale = locale;
  }

  operation.setContext({
    headers: {
      ...headers,
      ...headerSettings
    }
  });
  return forward(operation);
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );

      // Check for UNAUTHENTICATED error
      if (extensions?.code === 'UNAUTHENTICATED') {
        console.log('UNAUTHENTICATED error detected, redirecting to login');

        Cookies.remove('CF_Authorization');
        localStorage.clear();

        // Redirect to login
        window.location.href = getLoginUrl(window.location.origin, store.getters.enviroment.VUE_APP_SSO_CLIENT_ID);
      }

      return null;
    });
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);

    // Check if network error is also an authentication issue
    if (networkError.statusCode === 401) {
      console.log('401 Network error detected, redirecting to login');

      Cookies.remove('CF_Authorization');
      localStorage.clear();

      window.location.href = getLoginUrl(window.location.origin, store.getters.enviroment.VUE_APP_SSO_CLIENT_ID);
    }
  }
});

// Call this in the Vue app file
export default function createApolloClient (uri, name, options = {}) {
  // eslint-disable-next-line new-cap
  const httpLinkGateway = new createHttpLink({
  // You should use an absolute URL here
    uri: uri
  });

  const cache = new InMemoryCache({ addTypename: false });

  const linkGateway = ApolloLink.from([
    errorLink,
    headersLink,
    httpLinkGateway
  ]);

  return new ApolloClient({
    name: name,
    ...defaultOptions,
    ...options,
    link: linkGateway,
    cache: cache
  });
}
